import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { LANG_STORAGE_KEY, DEFAULT_LANG } from '@/i18n';

/**
 * Markaziy HTTP mijoz. Backend `{success, data, message, errorCode}` (ApiResponse)
 * qobig'ida javob qaytaradi — muvaffaqiyatli javoblarda `response.data` shu
 * qobiqdan avtomatik ochib olinadi, shuning uchun chaqiruvchi kod to'g'ridan-to'g'ri
 * payload bilan ishlaydi. Interceptorlar:
 *  - Accept-Language: joriy til
 *  - Authorization: saqlangan access token
 *  - 401 da: refresh token bilan bitta marta qayta urinish, muvaffaqiyatsiz bo'lsa logout
 */
export const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? '/api',
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const ACCESS_TOKEN_KEY = 'sqluz-token';
const REFRESH_TOKEN_KEY = 'sqluz-refresh-token';

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setAuthTokens(accessToken: string | null, refreshToken: string | null) {
  if (accessToken) localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  else localStorage.removeItem(ACCESS_TOKEN_KEY);

  if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  else localStorage.removeItem(REFRESH_TOKEN_KEY);
}

/** Backenddan kelgan xato (ApiResponse.fail) uchun foydalanuvchiga ko'rsatiladigan xabar. */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const message = (error.response?.data as { message?: string } | undefined)?.message;
    if (message) return message;
  }
  return fallback;
}

api.interceptors.request.use((config) => {
  const lang = localStorage.getItem(LANG_STORAGE_KEY) ?? DEFAULT_LANG;
  config.headers.set('Accept-Language', lang);

  const token = getAccessToken();
  if (token) config.headers.set('Authorization', `Bearer ${token}`);

  return config;
});

// Muvaffaqiyatli javoblarda ApiResponse qobig'ini ochib, to'g'ridan-to'g'ri `data`ni qaytaradi.
api.interceptors.response.use((response) => {
  const body = response.data as unknown;
  if (body && typeof body === 'object' && 'success' in body) {
    response.data = (body as unknown as { data: unknown }).data;
  }
  return response;
});

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  try {
    const res = await axios.post(
      `${api.defaults.baseURL}/auth/refresh`,
      { refreshToken },
      { headers: { 'Content-Type': 'application/json' } },
    );
    const body = res.data?.data ?? res.data;
    setAuthTokens(body.accessToken, body.refreshToken);
    return body.accessToken as string;
  } catch {
    setAuthTokens(null, null);
    return null;
  }
}

interface RetryableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetryableConfig | undefined;
    const isAuthEndpoint = original?.url?.includes('/auth/');

    if (error.response?.status === 401 && original && !original._retry && !isAuthEndpoint) {
      original._retry = true;
      refreshPromise ??= refreshAccessToken().finally(() => {
        refreshPromise = null;
      });
      const newToken = await refreshPromise;
      if (newToken) {
        original.headers.set('Authorization', `Bearer ${newToken}`);
        return api(original);
      }
    }

    if (error.response?.status === 401) {
      setAuthTokens(null, null);
    }
    return Promise.reject(error);
  },
);
